import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DiagramEditor = ({ onDiagramChange, initialDiagram }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [diagramData, setDiagramData] = useState(initialDiagram || '');
  const editorRef = useRef(null);

  const toolbarItems = [
    { id: 'select', icon: 'MousePointer', label: 'Select' },
    { id: 'rectangle', icon: 'Square', label: 'Rectangle' },
    { id: 'circle', icon: 'Circle', label: 'Circle' },
    { id: 'arrow', icon: 'ArrowRight', label: 'Arrow' },
    { id: 'text', icon: 'Type', label: 'Text' },
    { id: 'line', icon: 'Minus', label: 'Line' }
  ];

  const sapTemplates = [
    { id: 'process-flow', name: 'Process Flow', icon: 'GitBranch' },
    { id: 'org-structure', name: 'Org Structure', icon: 'Sitemap' },
    { id: 'data-flow', name: 'Data Flow', icon: 'Database' },
    { id: 'integration', name: 'Integration', icon: 'Shuffle' }
  ];

  useEffect(() => {
    // Simulate draw.io editor initialization
    if (editorRef?.current) {
      // In a real implementation, this would initialize the MXGraph editor
      console.log('Initializing draw.io editor');
    }
  }, []);

  const handleSaveDiagram = () => {
    // Simulate saving diagram data
    const mockDiagramXML = `<?xml version="1.0" encoding="UTF-8"?>
<mxfile host="app.diagrams.net" modified="2024-10-10T01:37:02.442Z">
  <diagram name="SAP Process Flow" id="diagram1">
    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1">
      <root>
        <mxCell id="0"/>
        <mxCell id="1" parent="0"/>
        <mxCell id="process1" value="Start Process" style="rounded=1;whiteSpace=wrap;html=1;" vertex="1" parent="1">
          <mxGeometry x="40" y="40" width="120" height="60" as="geometry"/>
        </mxCell>
      </root>
    </mxGraphModel>
  </diagram>
</mxfile>`;
    
    setDiagramData(mockDiagramXML);
    onDiagramChange(mockDiagramXML);
  };

  const handleTemplateSelect = (template) => {
    console.log('Loading template:', template?.name);
    // In real implementation, this would load the template into the editor
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="PenTool" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Diagram Editor</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            iconName={isFullscreen ? "Minimize2" : "Maximize2"}
            iconPosition="left"
          >
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSaveDiagram}
            iconName="Save"
            iconPosition="left"
          >
            Save Diagram
          </Button>
        </div>
      </div>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-2">
          {toolbarItems?.map((tool) => (
            <button
              key={tool?.id}
              className="p-2 rounded-md hover:bg-muted transition-colors"
              title={tool?.label}
            >
              <Icon name={tool?.icon} size={18} className="text-muted-foreground" />
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">SAP Templates:</span>
          {sapTemplates?.map((template) => (
            <button
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              className="flex items-center space-x-1 px-3 py-1 rounded-md bg-background hover:bg-muted transition-colors text-sm"
            >
              <Icon name={template?.icon} size={14} />
              <span>{template?.name}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Editor Canvas */}
      <div 
        ref={editorRef}
        className={`bg-white relative ${isFullscreen ? 'h-[calc(100vh-200px)]' : 'h-96'}`}
      >
        {/* Mock draw.io interface */}
        <div className="absolute inset-0 flex items-center justify-center border-2 border-dashed border-border">
          <div className="text-center">
            <Icon name="PenTool" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">
              Draw.io Editor Integration
            </h4>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">
              This area would contain the embedded draw.io editor with MXGraph XML support.\nUse the toolbar above to create SAP process diagrams.
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="p-4 bg-blue-50 rounded-lg">
                <Icon name="GitBranch" size={24} className="text-blue-600 mx-auto mb-2" />
                <p className="text-xs text-blue-700">Process Flow</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <Icon name="Database" size={24} className="text-green-600 mx-auto mb-2" />
                <p className="text-xs text-green-700">Data Model</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Properties Panel */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-foreground">Canvas Size:</span>
            <span className="text-muted-foreground ml-2">1200 x 800</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Grid:</span>
            <span className="text-muted-foreground ml-2">10px</span>
          </div>
          <div>
            <span className="font-medium text-foreground">Zoom:</span>
            <span className="text-muted-foreground ml-2">100%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagramEditor;